import { Theme } from '@mui/material'
import { SystemStyleObject } from '@mui/system'
import { PHASE_SHIFT, ValueWithLimits } from '../types/common'

/**
 * A helper function for Grid and System Protection settings number fields to determine if the field is editable
 * @param param0 ValueWithLimits object containing value, min, and max
 * @returns boolean indicating if the field should be disabled
 */
export const isFieldDisabled = ({ value, min, max }: Omit<ValueWithLimits, 'changed'>): boolean => {
  if (value === null) return true
  if (min !== null && max !== null && min === max) return true
  return false
}

/**
 * A helper function to determine whether a reactive power settings fulcrum's second value will be positive or negative depending on the phase shift value
 * @param value The second value of the fulcrum
 * @param phaseShift The phase shift value
 * @returns The actual value considering the phase shift
 */
export const valueDependingOnPhaseShift = (value: number | null, phaseShift: PHASE_SHIFT): number | null => {
  if (value === null) return null
  if (value === 0) return 0
  return phaseShift === PHASE_SHIFT.capacitive ? Math.abs(value) * -1 : Math.abs(value)
}

/**
 * A helper function to determine the phase shift based on the value
 * @param value The second value of the fulcrum
 * @returns The phase shift enum value
 */
export const phaseShiftDependingOnValue = (value: number | null): PHASE_SHIFT => {
  if (value === null) return PHASE_SHIFT.inductive
  return value >= 0 ? PHASE_SHIFT.inductive : PHASE_SHIFT.capacitive
}

/**
 * A helper function to add a highlight border for those fields that has been altered from the original values
 */
export const getChangedFieldStyle =
  (hasChanged: boolean) =>
  (theme: Theme): SystemStyleObject<Theme> => {
    if (hasChanged) {
      return {
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: theme.palette.warning.main,
            borderWidth: 2,
          },
        },
        '& .MuiSwitch-track': {
          border: `2px solid ${theme.palette.warning.main}`,
        },
      }
    }
    return {}
  }

const SYNC_ALERT_NAME_SPACE = 'systemSettings.grid-and-system-protection-configuration.sync-alert'

/** Removes the product prefix, keeping everything after the first underscore (e.g. "H20_2026_42" -> "2026_42"). */
const stripVersionPrefix = (version: string): string => version.slice(version.indexOf('_') + 1)

/**
 * Determines whether the given software versions are in sync. Comparison ignores the product
 * prefix (e.g. "H20_2026_42" and "XCM_2026_42" are considered equal); undefined versions are
 * ignored and a single (or no) version is always considered in sync.
 */
export const areSoftwareVersionsInSync = (softwareVersions: (string | undefined)[]): boolean => {
  const versions = softwareVersions.filter((version): version is string => Boolean(version)).map(stripVersionPrefix)
  if (versions.length <= 1) return true
  return versions.every(version => version === versions[0])
}

/**
 * Determines whether the grid-and-system-protection sync banner should be shown.
 */
export const shouldShowSyncBanner = ({
  areAllClientSettingsSynced,
  isSoftwareVersionOutOfSync,
  hasInstallationWritingRights,
}: {
  areAllClientSettingsSynced: boolean | undefined
  isSoftwareVersionOutOfSync: boolean
  hasInstallationWritingRights: boolean
}): boolean => (areAllClientSettingsSynced === false || isSoftwareVersionOutOfSync) && hasInstallationWritingRights

/**
 * Determines which translation key the sync alert should display, prioritizing a software
 * version mismatch over offline clients.
 */
export const getSyncAlertTranslationKey = ({
  isSoftwareVersionOutOfSync,
  offlineClientStorageIds,
}: {
  isSoftwareVersionOutOfSync: boolean
  offlineClientStorageIds: string[] | undefined
}): string => {
  if (isSoftwareVersionOutOfSync) return `${SYNC_ALERT_NAME_SPACE}.text-for-version-mismatch`
  if (offlineClientStorageIds && offlineClientStorageIds.length > 0) return `${SYNC_ALERT_NAME_SPACE}.text-for-offline-clients`
  return `${SYNC_ALERT_NAME_SPACE}.text`
}

/**
 * Determines whether the grid-and-system-protection settings forms are editable. A farm only allows
 * editing from the selected master, and editing is always blocked when client software versions are out of sync.
 */
export const canEditGridProtectionSettings = ({
  isFarm,
  isFarmMasterSelected,
  hasInstallationWritingRights,
  areSoftwareVersionsInSync: areVersionsInSync,
}: {
  isFarm: boolean
  isFarmMasterSelected: boolean
  hasInstallationWritingRights: boolean
  areSoftwareVersionsInSync: boolean
}): boolean => (isFarm ? hasInstallationWritingRights && isFarmMasterSelected : hasInstallationWritingRights) && areVersionsInSync
